import { useState, useEffect } from "react";
import { addEventTask, updateEventTask, deleteEventTask, generateEventTasks } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Sparkles, ListChecks, ChevronDown, ChevronUp, X, Package, Users } from "lucide-react";

function TypeIcon({ type }) {
  if (type === "crew") return <Users className="w-3 h-3 text-blue-400 shrink-0" />;
  if (type === "subrental") return <Package className="w-3 h-3 text-amber-400 shrink-0" />;
  return <ListChecks className="w-3 h-3 text-muted-foreground shrink-0" />;
}

export function TaskChecklist({ event, canEdit }) {
  const [tasks, setTasks] = useState(event.tasks || []);
  const [newTask, setNewTask] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { setTasks(event.tasks || []); }, [event.tasks]);

  const completedCount = tasks.filter(t => t.completed).length;
  const totalCount = tasks.length;

  const handleToggle = async (taskId, completed) => {
    try {
      await updateEventTask(event.event_id, taskId, { completed: !completed });
      setTasks(prev => prev.map(t => t.task_id === taskId ? { ...t, completed: !completed } : t));
    } catch { toast.error("Failed to update task"); }
  };

  const handleAdd = async () => {
    if (!newTask.trim()) return;
    try {
      const res = await addEventTask(event.event_id, { description: newTask, task_type: "general" });
      setTasks(prev => [...prev, res.data]);
      setNewTask("");
    } catch { toast.error("Failed to add task"); }
  };

  const handleDelete = async (taskId) => {
    try {
      await deleteEventTask(event.event_id, taskId);
      setTasks(prev => prev.filter(t => t.task_id !== taskId));
    } catch { toast.error("Failed to delete task"); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await generateEventTasks(event.event_id);
      setTasks(res.data);
      toast.success("Tasks generated");
    } catch { toast.error("Failed to generate tasks"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="mt-3 pt-2 border-t border-border">
      <button
        data-testid={`tasks-toggle-${event.event_id}`}
        className="flex items-center justify-between w-full text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <ListChecks className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Tasks {totalCount > 0 && `(${completedCount}/${totalCount})`}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5">
          {totalCount > 0 && (
            <div className="w-full h-1 bg-secondary rounded-full overflow-hidden mb-2">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }} />
            </div>
          )}

          {tasks.map(t => (
            <div key={t.task_id} data-testid={`task-${t.task_id}`} className={`flex items-center gap-2 py-1 px-2 rounded-sm text-sm ${t.completed ? 'opacity-50' : ''} hover:bg-secondary/20`}>
              <Checkbox
                data-testid={`task-check-${t.task_id}`}
                checked={t.completed}
                onCheckedChange={() => handleToggle(t.task_id, t.completed)}
                disabled={!canEdit}
              />
              <TypeIcon type={t.task_type} />
              <span className={`flex-1 text-xs ${t.completed ? 'line-through text-muted-foreground' : ''}`}>{t.description}</span>
              {canEdit && (
                <button data-testid={`task-delete-${t.task_id}`} onClick={() => handleDelete(t.task_id)} className="text-muted-foreground hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}

          {canEdit && (
            <div className="flex items-center gap-2 mt-2">
              <Input
                data-testid={`task-input-${event.event_id}`}
                placeholder="Add a task..."
                value={newTask}
                onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                className="h-7 text-xs bg-secondary/30 border-border flex-1"
              />
              <Button data-testid={`task-add-${event.event_id}`} variant="secondary" size="sm" className="h-7 text-xs px-2" onClick={handleAdd}>
                <Plus className="w-3 h-3" />
              </Button>
              <Button data-testid={`task-generate-${event.event_id}`} variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={handleGenerate} disabled={generating} title="Auto-detect missing crew & sub-rentals">
                <Sparkles className="w-3 h-3" />
              </Button>
            </div>
          )}

          {tasks.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">
              No tasks yet. {canEdit && 'Click the sparkle icon to auto-detect needs.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
